import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useUploadVesselInfo } from "../utils/uploadvessel_primary_info";
import Spinner from "../../Utils/spinner/loading";
import { toast, ToastContainer } from "react-toastify";
import useFetchVesselUses from "../utils/useFetchVesselUses";
import useReadVesselServiceTypes from "../utils/useFetchVesselServiceTypes";
import { ReactComponent as VatugIcon } from "../../Asset/black.svg";
import { HiX } from 'react-icons/hi';
import { HiArrowLeft } from 'react-icons/hi';
import "react-toastify/dist/ReactToastify.css";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import "../create_vessels/createvessel.css";

const defaultForm = {
  vesselDescription: "",
  vesselUse: [],
  serviceType: "",
  pricing: "",
  paymentPlan: "",
  negotiable: null,
};

const VesselInformation = () => {
  const navigate = useNavigate();
  const [formField, setFormField] = useState(defaultForm);
  const { mutate, isLoading, error: mutationError } = useUploadVesselInfo();
  const vesselType = localStorage.getItem("selectedVesselType");

  const { data: vesselUsesData, isLoading: isFetchingVesselUses } =
    useFetchVesselUses();
  const {
    data: vesselServiceTypesData,
    isLoading: isFetchingVesselServiceTypes,
  } = useReadVesselServiceTypes();

  // Custom dropdown states
  const [isVesselUseOpen, setIsVesselUseOpen] = useState(false);
  const [isServiceTypeOpen, setIsServiceTypeOpen] = useState(false);
  const [isPaymentPlanOpen, setIsPaymentPlanOpen] = useState(false);
  const [isNegotiableOpen, setIsNegotiableOpen] = useState(false);
  
  const [vesselUseSearchTerm, setVesselUseSearchTerm] = useState("");
  const [serviceTypeSearchTerm, setServiceTypeSearchTerm] = useState("");
  const [paymentPlanSearchTerm, setPaymentPlanSearchTerm] = useState("");
  const [negotiableSearchTerm, setNegotiableSearchTerm] = useState("");
  
  // Refs for click outside handling
  const vesselUseRef = useRef(null);
  const serviceTypeRef = useRef(null);
  const paymentPlanRef = useRef(null);
  const negotiableRef = useRef(null);

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (vesselUseRef.current && !vesselUseRef.current.contains(event.target)) {
        setIsVesselUseOpen(false);
      }
      if (serviceTypeRef.current && !serviceTypeRef.current.contains(event.target)) {
        setIsServiceTypeOpen(false);
      }
      if (paymentPlanRef.current && !paymentPlanRef.current.contains(event.target)) {
        setIsPaymentPlanOpen(false);
      }
      if (negotiableRef.current && !negotiableRef.current.contains(event.target)) {
        setIsNegotiableOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Toggle dropdowns
  const toggleVesselUseDropdown = () => {
    setIsVesselUseOpen(!isVesselUseOpen);
    setIsServiceTypeOpen(false);
    setIsPaymentPlanOpen(false);
    setIsNegotiableOpen(false);
  };

  const toggleServiceTypeDropdown = () => {
    setIsServiceTypeOpen(!isServiceTypeOpen);
    setIsVesselUseOpen(false);
    setIsPaymentPlanOpen(false);
    setIsNegotiableOpen(false);
  };

  const togglePaymentPlanDropdown = () => {
    setIsPaymentPlanOpen(!isPaymentPlanOpen);
    setIsVesselUseOpen(false);
    setIsServiceTypeOpen(false);
    setIsNegotiableOpen(false);
  };

  const toggleNegotiableDropdown = () => {
    setIsNegotiableOpen(!isNegotiableOpen);
    setIsVesselUseOpen(false);
    setIsServiceTypeOpen(false);
    setIsPaymentPlanOpen(false);
  };

  // Filter options based on search terms
  const filteredVesselUses = vesselUsesData?.data?.filter(use => 
    use.toLowerCase().includes(vesselUseSearchTerm.toLowerCase())
  ) || [];

  const filteredServiceTypes = vesselServiceTypesData?.data?.filter(type => 
    type.toLowerCase().includes(serviceTypeSearchTerm.toLowerCase())
  ) || [];

  // Payment plan options
  const paymentPlanOptions = [...Array(12)].map((_, index) => ({
    value: index + 1,
    label: `${index + 1} month${index + 1 > 1 ? "s" : ""}`
  }));

  const filteredPaymentPlans = paymentPlanOptions.filter(plan => 
    plan.label.toLowerCase().includes(paymentPlanSearchTerm.toLowerCase())
  );

  // Negotiable options
  const negotiableOptions = [
    { value: true, label: "Yes" },
    { value: false, label: "No" }
  ];

  const filteredNegotiableOptions = negotiableOptions.filter(option => 
    option.label.toLowerCase().includes(negotiableSearchTerm.toLowerCase())
  );

  // Handle vessel use selection (now supports multiple selections)
  const handleVesselUseSelect = (use) => {
    // Check if the use is already selected
    if (!formField.vesselUse.includes(use)) {
      setFormField({
        ...formField,
        vesselUse: [...formField.vesselUse, use]
      });
    }
    setVesselUseSearchTerm("");
    // Don't close the dropdown after selection to allow multiple selections
  };

  // Handle removing a vessel use selection
  const handleRemoveVesselUse = (useToRemove) => {
    setFormField({
      ...formField,
      vesselUse: formField.vesselUse.filter(use => use !== useToRemove)
    });
  };

  // Handle service type selection
  const handleServiceTypeSelect = (type) => {
    setFormField({
      ...formField,
      serviceType: type
    });
    setIsServiceTypeOpen(false);
    setServiceTypeSearchTerm("");
    
    // Force state update to ensure dropdown closes
    setTimeout(() => {
      setIsServiceTypeOpen(false);
    }, 0);
  };

  // Handle payment plan selection
  const handlePaymentPlanSelect = (value) => {
    setFormField({
      ...formField,
      paymentPlan: value
    });
    setIsPaymentPlanOpen(false);
    setPaymentPlanSearchTerm("");
    
    // Force state update to ensure dropdown closes
    setTimeout(() => {
      setIsPaymentPlanOpen(false);
    }, 0);
  };

  // Handle negotiable selection
  const handleNegotiableSelect = (value) => {
    setFormField({
      ...formField,
      negotiable: value
    });
    setIsNegotiableOpen(false);
    setNegotiableSearchTerm("");
    
    // Force state update to ensure dropdown closes
    setTimeout(() => {
      setIsNegotiableOpen(false);
    }, 0);
  };

  useEffect(() => {
    if (mutationError) {
      toast.error(
        mutationError.message ||
          "An error occurred during submission. Please try again."
      );
    }
  }, [mutationError]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "pricing") {
      setFormField({
        ...formField,
        [name]: parseFloat(value),
      });
    } else {
      setFormField({
        ...formField,
        [name]: value,
      });
    }
  };

  const validateForm = () => {
    const {
      vesselDescription,
      vesselUse,
      serviceType,
      pricing,
      paymentPlan,
      negotiable,
    } = formField;
    if (
      !vesselType ||
      !vesselDescription ||
      !vesselUse || 
      vesselUse.length < 2 || // Ensure at least 2 vessel uses are selected
      !serviceType ||
      !pricing ||
      !paymentPlan ||
      negotiable === null
    ) {
      return "All fields are required and must be valid. Vessel Use requires at least 2 selections.";
    }
    return null;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const formError = validateForm();
    if (formError) {
      toast.error(formError);
      return;
    }
    mutate(
      { formData: formField, vesselType },
      {
        onError: (err) => {
          console.error(err);
        },
      }
    );
  };

  const {
    vesselDescription,
    vesselUse,
    serviceType,
    pricing,
    paymentPlan,
    negotiable,
  } = formField;

  // Helper function to get payment plan label
  const getPaymentPlanLabel = () => {
    if (!paymentPlan) return "Select Payment Plan";
    const option = paymentPlanOptions.find(opt => opt.value === paymentPlan);
    return option ? option.label : "Select Payment Plan";
  };

  // Helper function to get negotiable label
  const getNegotiableLabel = () => {
    if (negotiable === null) return "Select Negotiable";
    return negotiable ? "Yes" : "No";
  };

  const handleGoBack = () => {
    navigate("/dashboard/myvessels");
  };

  return (
    <div className="create-vessel-container">
      <div className="create-vessel-header-bar">
        <div className="logo-container">
          <VatugIcon />
          <div className="logo-underline"></div>
        </div>
        <div className="title">Vessel Upload</div>
        <button className="close-btn" onClick={handleGoBack}> <HiX /> </button>
      </div>

      <div className="create-vessel-card">
        <h2>Vessel Information</h2>
        
        <label>
          Vessel Type
          <input
            type="text"
            name="vesselType"
            value={vesselType}
            onChange={handleChange}
            disabled
            readOnly
          />
        </label>

        <label>
          Vessel Description
          <input
            type="text"
            name="vesselDescription"
            value={vesselDescription}
            onChange={handleChange}
            placeholder="Enter"
          />
        </label>

        <label>
          Vessel Use (minimum 2 selections)
          {isFetchingVesselUses ? (
            <Skeleton height={40} width="100%" />
          ) : (
            <div className="custom-select-container" ref={vesselUseRef}>
              <button 
                type="button"
                className="custom-select-button"
                onClick={toggleVesselUseDropdown}
              >
                Select Vessel Use
              </button>
              
              {vesselUse.length > 0 && (
                <div className="selected-items-container">
                  {vesselUse.map((use, index) => (
                    <div key={index} className="selected-item">
                      <span>{use}</span>
                      <button 
                        type="button" 
                        className="remove-item-btn"
                        onClick={() => handleRemoveVesselUse(use)}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {isVesselUseOpen && (
                <div className="custom-select-dropdown show">
                  <div className="custom-select-search">
                    <input
                      type="text"
                      placeholder="Search vessel use..."
                      value={vesselUseSearchTerm}
                      onChange={(e) => setVesselUseSearchTerm(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  {filteredVesselUses.length > 0 ? (
                    filteredVesselUses.map((use, index) => (
                      <div
                        key={index}
                        className={`custom-select-option ${vesselUse.includes(use) ? 'selected' : ''}`}
                        onClick={() => handleVesselUseSelect(use)}
                      >
                        {use}
                      </div>
                    ))
                  ) : (
                    <div className="custom-select-option">No vessel uses found</div>
                  )}
                </div>
              )}
            </div>
          )}
        </label>

        <label>
          Service Type
          {isFetchingVesselServiceTypes ? (
            <Skeleton height={40} width="100%" />
          ) : (
            <div className="custom-select-container" ref={serviceTypeRef}>
              <button 
                type="button"
                className="custom-select-button"
                onClick={toggleServiceTypeDropdown}
              >
                {serviceType || "Select Service Type"}
              </button>
              {isServiceTypeOpen && (
                <div className="custom-select-dropdown show">
                  <div className="custom-select-search">
                    <input
                      type="text"
                      placeholder="Search service type..."
                      value={serviceTypeSearchTerm}
                      onChange={(e) => setServiceTypeSearchTerm(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  {filteredServiceTypes.length > 0 ? (
                    filteredServiceTypes.map((type, index) => (
                      <div
                        key={index}
                        className={`custom-select-option ${serviceType === type ? 'selected' : ''}`}
                        onClick={() => handleServiceTypeSelect(type)}
                      >
                        {type}
                      </div>
                    ))
                  ) : (
                    <div className="custom-select-option">No service types found</div>
                  )}
                </div>
              )}
            </div>
          )}
        </label>

        <label>
          Pricing ($)
          <input
            type="text"
            name="pricing"
            value={pricing}
            onChange={handleChange}
            placeholder="Enter"
          />
        </label>

        <label>
          Payment Plan (Monthly)
          <div className="custom-select-container" ref={paymentPlanRef}>
            <button 
              type="button"
              className="custom-select-button"
              onClick={togglePaymentPlanDropdown}
            >
              {getPaymentPlanLabel()}
            </button>
            {isPaymentPlanOpen && (
              <div className="custom-select-dropdown show">
                <div className="custom-select-search">
                  <input
                    type="text"
                    placeholder="Search payment plan..."
                    value={paymentPlanSearchTerm}
                    onChange={(e) => setPaymentPlanSearchTerm(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                {filteredPaymentPlans.length > 0 ? (
                  filteredPaymentPlans.map((plan, index) => (
                    <div
                      key={index}
                      className={`custom-select-option ${paymentPlan === plan.value ? 'selected' : ''}`}
                      onClick={() => handlePaymentPlanSelect(plan.value)}
                    >
                      {plan.label}
                    </div>
                  ))
                ) : (
                  <div className="custom-select-option">No payment plans found</div>
                )}
              </div>
            )}
          </div>
        </label>

        <label>
          Negotiable
          <div className="custom-select-container" ref={negotiableRef}>
            <button 
              type="button"
              className="custom-select-button"
              onClick={toggleNegotiableDropdown}
            >
              {getNegotiableLabel()}
            </button>
            {isNegotiableOpen && (
              <div className="custom-select-dropdown show">
                <div className="custom-select-search">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={negotiableSearchTerm}
                    onChange={(e) => setNegotiableSearchTerm(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                {filteredNegotiableOptions.length > 0 ? (
                  filteredNegotiableOptions.map((option, index) => (
                    <div
                      key={index}
                      className={`custom-select-option ${negotiable === option.value ? 'selected' : ''}`}
                      onClick={() => handleNegotiableSelect(option.value)}
                    >
                      {option.label}
                    </div>
                  ))
                ) : (
                  <div className="custom-select-option">No options found</div>
                )}
              </div>
            )}
          </div>
        </label>

        <div className="button-row">
          <button className="back-btn" onClick={handleGoBack}>
            <HiArrowLeft style={{marginRight: 6}}/> Go Back
          </button>
          <button 
            className="next-btn" 
            onClick={handleSubmit} 
            disabled={isLoading}
          >
            {isLoading ? <Spinner /> : "Next"}
          </button>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default VesselInformation;
